/**
 * 图表色板 —— 全模块唯一来源
 * ==================================================================
 * 之前每个图表各写各的蓝（#4080ff / #165dff / #94bfff / #7aa5ff /
 * #cfe3ff / #5a8dff…），同一语义在不同图上颜色都不一样。这里一次性收口，
 * **所有 plots / echarts 的取色都必须从这里拿**，不要在组件里写字面量。
 *
 * 取值来自商家端 h5（manage/guidance-dashboard.vue 的 echarts 配置），
 * 保证后台与商家端同一指标同色。
 */

/** 分类系列色（按顺序取，第 n 个系列用 CHART_SERIES[n]） */
export const CHART_SERIES = [
  '#1890ff', // 主数据：销售、到店、新增
  '#5bc9ca', // 对比系列：核销、仅线上打卡
  '#dc9a15', // 金
  '#f56c6c', // 红
  '#8b5cf6', // 紫
  '#ec4899', // 粉
  '#00c332', // 绿
  '#fa8c16', // 橙
  '#13c2c2', // 青
  '#722ed1', // 深紫
];

/** 语义色：同一含义永远同一颜色 */
export const CHART_SEMANTIC = {
  /** 销售 / 新增 / 正向 */
  primary: '#1890ff',
  /** 核销 / 消耗 / 对比系列 */
  secondary: '#5bc9ca',
  /** 未完成 / 风险 / 流失 */
  danger: '#f56c6c',
  /** 预警 / 待跟进 */
  warning: '#dc9a15',
  /** 已完成 / 健康 */
  success: '#00c332',
  /** 中性 */
  neutral: '#c9cdd4',
  /** 图形内部填充（折线图的圆点、标签底色等需要“挖空”的底） */
  surface: '#ffffff',
};

/**
 * 坐标轴 / 网格色
 * ------------------------------------------------------------------
 * 之前各图自写 #e5e6eb / #c9cdd4 / #86909c / #4e5969 这类字面量，
 * 与 theme.less 的 @pg-* 重复但没人管。统一从这里取。
 * 右上角注释的 less 变量是其对应 token，两边改动需同步。
 */
export const CHART_AXIS = {
  /** @pg-border */
  grid: '#e5e6eb',
  /** @pg-text-disabled */
  axisLine: '#c9cdd4',
  /** @pg-text-tertiary */
  axisLabel: '#86909c',
  /** @pg-text-secondary */
  valueLabel: '#4e5969',
  /** @pg-text-primary —— 图形中心的主数值/标题（如环形图中心数字） */
  strongLabel: '#1d2129',
  /** 交替行/背景填充（雷达图 alternateColor 等），4% 黑 */
  altFill: 'rgba(0, 0, 0, 0.04)',
};

/**
 * 漏斗 / 排名条的蓝色渐变（图3 反馈里你觉得好看的正是这个）
 * 第 1 级最浅、逐级加深，统一用这一组，不要再另配。
 */
export const CHART_RAMP = [
  '#d6e6ff',
  '#b3cdff',
  '#8fb0ff',
  '#6d94ff',
  '#4a78ff',
  '#165dff',
];

/** 面积图渐变（h5 同款：顶部 30% → 底部 5%） */
export const areaGradient = (hex) => {
  const { r, g, b } = toRgb(hex);
  return {
    gradient: 'y',
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: `rgba(${r},${g},${b},0.3)` },
      { offset: 1, color: `rgba(${r},${g},${b},0.05)` },
    ],
  };
};

/** rgba 字符串版，供 CSS / svg 用 */
export const rgba = (hex, alpha = 1) => {
  const { r, g, b } = toRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
};

/** 取第 i 个系列色 */
export const seriesColor = (i) => CHART_SERIES[i % CHART_SERIES.length];

/** 取渐变带上第 i 档（超出取最深） */
export const rampColor = (i) => CHART_RAMP[Math.min(i, CHART_RAMP.length - 1)];

/** 渐变带起止（横向条用 left→right） */
export const rampGradient = (i) => {
  const from = rampColor(Math.max(0, i - 1));
  const to = rampColor(i);
  return `linear-gradient(90deg, ${from}, ${to})`;
};

function toRgb(hex) {
  const h = String(hex).replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

export default {
  CHART_SERIES,
  CHART_SEMANTIC,
  CHART_AXIS,
  CHART_RAMP,
  areaGradient,
  rgba,
  seriesColor,
  rampColor,
  rampGradient,
};
